import React, { useState } from "react";
import {
  formatTime,
  formatTimeDetailed,
  formatDate,
  parseTimeInput,
} from "../utils/helpers";
import { sessionManager } from "../utils/sessionManager";

const SessionItem = ({ session, onEdit, onDelete, onDuplicate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editDuration, setEditDuration] = useState(
    formatTimeDetailed(session.duration)
  );

  const handleSave = async () => {
    try {
      // Parse duration using the helper function
      const totalSeconds = parseTimeInput(editDuration);
      if (totalSeconds <= 0) {
        alert(
          'Invalid time format. Use any combination like "2h 30m", "30m", "10s", "1h", "45m 30s"'
        );
        return;
      }

      await sessionManager.updateSession(session.id, {
        duration: totalSeconds,
      });

      setIsEditing(false);
      onEdit && onEdit();
    } catch (error) {
      alert("Error updating session: " + error.message);
    }
  };

  const handleCancel = () => {
    setEditDuration(formatTimeDetailed(session.duration));
    setIsEditing(false);
  };

  // const taskInfo = getTaskInfo(); // Currently unused but kept for future use

  return (
    <div className={`session-item ${session.type}`}>
      <div className="session-header">
        <div className="session-info">
          <div className="session-time">
            <small>{session.type === "timer" ? "Timer" : "Manual"}</small>
            <span className="duration">
              {formatTimeDetailed(session.duration)}
            </span>
            <span className="datetime">
              {(() => {
                console.log(
                  "SessionList: Formatting datetime for session:",
                  session.id,
                  "createdAt:",
                  session.createdAt
                );
                const dateStr = formatDate(session.createdAt);
                const timeStr = session.createdAt
                  ? new Date(session.createdAt).toLocaleTimeString()
                  : "Unknown time";
                console.log(
                  "SessionList: Formatted date:",
                  dateStr,
                  "time:",
                  timeStr
                );
                return `${dateStr} at ${timeStr}`;
              })()}
            </span>
          </div>
        </div>

        <div className="session-actions">
          <button
            className="edit-btn"
            onClick={() => setIsEditing(!isEditing)}
            title="Edit session"
          >
            Edit
          </button>
          <button
            className="duplicate-btn"
            onClick={() => onDuplicate && onDuplicate(session)}
            title="Duplicate session"
          >
            Copy
          </button>
          <button
            className="delete-btn"
            onClick={() => onDelete && onDelete(session.id)}
            title="Delete session"
          >
            Delete
          </button>
        </div>
      </div>

      {isEditing ? (
        <div className="session-edit-form">
          <div className="edit-row">
            <label>Duration:</label>
            <input
              type="text"
              value={editDuration}
              onChange={(e) => setEditDuration(e.target.value)}
              placeholder="Any combination: 2h 30m, 30m, 10s, 1h, 45m 30s"
            />
          </div>
          <div className="edit-actions">
            <button className="save-btn" onClick={handleSave}>
              Save
            </button>
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const SessionList = ({
  sessions = [],
  projects = [],
  tasks = [],
  onSessionChange,
  showStats = true,
  showGrouping = false,
}) => {
  const [groupBy, setGroupBy] = useState("none");
  const [sortBy, setSortBy] = useState("date");

  const handleDeleteSession = async (sessionId) => {
    if (window.confirm("Delete this session? This cannot be undone.")) {
      try {
        await sessionManager.deleteSession(sessionId);
        onSessionChange && onSessionChange();
      } catch (error) {
        alert("Error deleting session: " + error.message);
      }
    }
  };

  const handleDuplicateSession = async (session) => {
    try {
      await sessionManager.duplicateSession(session.id);
      onSessionChange && onSessionChange();
    } catch (error) {
      alert("Error duplicating session: " + error.message);
    }
  };

  const handleEditSession = () => {
    onSessionChange && onSessionChange();
  };

  // Sort sessions
  const sortedSessions = [...sessions].sort((a, b) => {
    switch (sortBy) {
      case "date":
        return new Date(b.createdAt) - new Date(a.createdAt);
      case "duration":
        return b.duration - a.duration;
      case "type":
        return a.type.localeCompare(b.type);
      default:
        return 0;
    }
  });

  // Group sessions
  const groupedSessions = (() => {
    if (groupBy === "date") {
      return sessionManager.groupSessionsByDate(sortedSessions);
    } else if (groupBy === "project") {
      return sessionManager.groupSessionsByProject(
        sortedSessions,
        tasks,
        projects
      );
    } else {
      return { "All Sessions": sortedSessions };
    }
  })();

  const stats = sessionManager.getSessionStats(sessions);
  const timeBreakdown = sessionManager.calculateTimeByType(sessions);

  return (
    <div className="session-list">
      {showStats && (
        <div className="session-stats">
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total Time:</span>
              <span className="stat-value">{formatTime(stats.totalTime)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Manual Time:</span>
              <span className="stat-value">
                {formatTime(timeBreakdown.manual)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Timer Time:</span>
              <span className="stat-value">
                {formatTime(timeBreakdown.timer)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Sessions:</span>
              <span className="stat-value">{stats.totalSessions}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Average Session:</span>
              <span className="stat-value">
                {formatTime(stats.averageSession)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Longest Session:</span>
              <span className="stat-value">
                {formatTime(stats.longestSession)}
              </span>
            </div>
          </div>
        </div>
      )}

      {showGrouping && (
        <div className="session-controls">
          <div className="control-group">
            <label>Group by:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="none">No Grouping</option>
              <option value="date">Date</option>
              <option value="project">Project</option>
            </select>
          </div>
          <div className="control-group">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date">Date</option>
              <option value="duration">Duration</option>
              <option value="type">Type</option>
            </select>
          </div>
        </div>
      )}

      <div className="sessions-container">
        {Object.entries(groupedSessions).map(([groupName, groupSessions]) => (
          <div key={groupName} className="session-group">
            {groupBy !== "none" && (
              <div className="group-header">
                <h4>{groupName}</h4>
                <span className="group-stats">
                  {groupSessions.length} sessions |{" "}
                  {formatTime(sessionManager.calculateTotalTime(groupSessions))}
                </span>
              </div>
            )}

            <div className="sessions-list">
              {groupSessions.map((session, index) => {
                console.log(
                  "SessionList: Rendering session:",
                  session,
                  "index:",
                  index,
                  "id:",
                  session.id
                );
                return (
                  <SessionItem
                    key={session.id || `session-${index}`}
                    session={session}
                    projects={projects}
                    tasks={tasks}
                    onEdit={handleEditSession}
                    onDelete={handleDeleteSession}
                    onDuplicate={handleDuplicateSession}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {sessions.length === 0 && (
        <div className="empty-state">
          <p>No sessions found. Start tracking time to see sessions here.</p>
        </div>
      )}
    </div>
  );
};

export default SessionList;
